import {ReplyIcon, TrashIcon} from '@primer/octicons-react'
import {Button, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {observeClientInteraction} from '@github-ui/ahp-relay/relay-command-observation'
import {memo, useCallback, useEffect, useRef, useState, type ReactNode, type Ref, type RefObject} from 'react'

import styles from './QueuedMessageControl.module.css'

export type QueuedMessageControlItemId = string | number

export interface QueuedMessageControlItem {
  /** Stable opaque identifier used only for keys, callbacks, and internal bookkeeping. */
  id: QueuedMessageControlItemId
  /** Fully rendered message content. */
  content: ReactNode
  /** Human-readable description used to distinguish this message's actions. */
  accessibleLabel: string
  removePending?: boolean
  removeDisabled?: boolean
  sendNowPending?: boolean
  sendNowDisabled?: boolean
}

export interface QueuedMessageControlProps {
  items: readonly QueuedMessageControlItem[]
  onRemove?: (id: QueuedMessageControlItemId) => void
  onSendNow?: (id: QueuedMessageControlItemId) => void
  onSendNowIntent?: (id: QueuedMessageControlItemId, event: Event, button: HTMLButtonElement) => void
  readOnly?: boolean
  expanded?: boolean
  defaultExpanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  ariaLabel?: string
  returnFocusRef?: RefObject<HTMLElement | null>
  onFocusFallback?: () => void
  className?: string
  ref?: Ref<HTMLDivElement>
}

type ActionName = 'remove' | 'sendNow'

interface FocusedAction {
  action: ActionName
  itemId: QueuedMessageControlItemId
}

interface FocusSnapshot {
  itemIds: readonly QueuedMessageControlItemId[]
  removeVisible: boolean
  sendNowVisible: boolean
}

type ActionButtons = Partial<Record<ActionName, HTMLButtonElement>>

function includesItem(snapshot: FocusSnapshot, itemId: QueuedMessageControlItemId) {
  return snapshot.itemIds.includes(itemId)
}

function includesAction(snapshot: FocusSnapshot, focusedAction: FocusedAction) {
  if (!includesItem(snapshot, focusedAction.itemId)) return false
  return focusedAction.action === 'remove' ? snapshot.removeVisible : snapshot.sendNowVisible
}

function isBrowserFocusLost() {
  const activeElement = document.activeElement
  return activeElement === null || activeElement === document.body || activeElement === document.documentElement
}

function getNeighborIds(
  previousItemIds: readonly QueuedMessageControlItemId[],
  currentItemIds: readonly QueuedMessageControlItemId[],
  focusedItemId: QueuedMessageControlItemId,
) {
  const previousIndex = previousItemIds.indexOf(focusedItemId)
  const currentIndex = currentItemIds.indexOf(focusedItemId)
  const pivot = currentIndex >= 0 ? currentIndex : Math.min(Math.max(previousIndex, 0), currentItemIds.length)
  const nextIds = currentItemIds.slice(currentIndex >= 0 ? pivot + 1 : pivot)
  const previousIds = currentItemIds.slice(0, pivot).reverse()

  return [...nextIds, ...previousIds]
}

interface QueuedMessageItemProps {
  item: QueuedMessageControlItem
  itemCount: number
  itemPosition: number
  onActionFocus: (itemId: QueuedMessageControlItemId, action: ActionName) => void
  onActionRef: (itemId: QueuedMessageControlItemId, action: ActionName, node: HTMLButtonElement | null) => void
  onRemove?: (id: QueuedMessageControlItemId) => void
  onSendNow?: (id: QueuedMessageControlItemId) => void
  onSendNowIntent?: QueuedMessageControlProps['onSendNowIntent']
}

const QueuedMessageItem = memo(function QueuedMessageItemComponent({
  item,
  itemCount,
  itemPosition,
  onActionFocus,
  onActionRef,
  onRemove,
  onSendNow,
  onSendNowIntent,
}: QueuedMessageItemProps) {
  const sendNowRef = useCallback(
    (node: HTMLButtonElement | null) => onActionRef(item.id, 'sendNow', node),
    [item.id, onActionRef],
  )
  const removeRef = useCallback(
    (node: HTMLButtonElement | null) => onActionRef(item.id, 'remove', node),
    [item.id, onActionRef],
  )
  const itemContext = `queued message ${itemPosition} of ${itemCount}: ${item.accessibleLabel}`

  return (
    <li className={styles.ListItem}>
      <div className={styles.Content}>{item.content}</div>
      <div className={styles.Actions}>
        {onSendNow ? (
          <IconButton
            ref={sendNowRef}
            className={styles.Action}
            icon={ReplyIcon}
            variant="invisible"
            size="small"
            aria-label={`Send now, ${itemContext}`}
            description={item.sendNowPending ? undefined : 'Send now'}
            loading={Boolean(item.sendNowPending)}
            loadingAnnouncement={`Sending now, ${itemContext}`}
            aria-disabled={item.sendNowDisabled || item.sendNowPending || undefined}
            inactive={item.sendNowDisabled || item.sendNowPending || undefined}
            onFocusCapture={() => onActionFocus(item.id, 'sendNow')}
            onClick={event => {
              if (!item.sendNowDisabled && !item.sendNowPending) {
                if (onSendNowIntent)
                  observeClientInteraction(() => onSendNowIntent(item.id, event.nativeEvent, event.currentTarget))
                onSendNow(item.id)
              }
            }}
          />
        ) : null}
        {onRemove ? (
          <IconButton
            ref={removeRef}
            className={styles.Action}
            icon={TrashIcon}
            variant="invisible"
            size="small"
            aria-label={`Remove from queue, ${itemContext}`}
            description={item.removePending ? undefined : 'Remove from queue'}
            loading={Boolean(item.removePending)}
            loadingAnnouncement={`Removing from queue, ${itemContext}`}
            aria-disabled={item.removeDisabled || item.removePending || undefined}
            inactive={item.removeDisabled || item.removePending || undefined}
            onFocusCapture={() => onActionFocus(item.id, 'remove')}
            onClick={() => {
              if (!item.removeDisabled && !item.removePending) onRemove(item.id)
            }}
          />
        ) : null}
      </div>
    </li>
  )
})

QueuedMessageItem.displayName = 'QueuedMessageItem'

export function QueuedMessageControl({
  items,
  onRemove,
  onSendNow,
  onSendNowIntent,
  readOnly = false,
  expanded,
  defaultExpanded = false,
  onExpandedChange,
  ariaLabel = 'Queued messages',
  returnFocusRef,
  onFocusFallback,
  className,
  ref,
}: QueuedMessageControlProps) {
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded)
  const isControlled = expanded !== undefined
  const isExpanded = isControlled ? expanded : uncontrolledExpanded
  const triggerRef = useRef<HTMLButtonElement>(null)
  const actionButtonsRef = useRef(new Map<QueuedMessageControlItemId, ActionButtons>())
  const focusedActionRef = useRef<FocusedAction | null>(null)
  const previousSnapshotRef = useRef<FocusSnapshot | null>(null)

  const actionsVisible = isExpanded && !readOnly
  const removeVisible = actionsVisible && onRemove !== undefined
  const sendNowVisible = actionsVisible && onSendNow !== undefined

  const handleActionFocus = useCallback((itemId: QueuedMessageControlItemId, action: ActionName) => {
    focusedActionRef.current = {itemId, action}
  }, [])

  const handleActionRef = useCallback(
    (itemId: QueuedMessageControlItemId, action: ActionName, node: HTMLButtonElement | null) => {
      const actionButtons = actionButtonsRef.current.get(itemId) ?? {}

      if (node) {
        actionButtons[action] = node
        actionButtonsRef.current.set(itemId, actionButtons)
      } else {
        delete actionButtons[action]
        if (actionButtons.remove || actionButtons.sendNow) {
          actionButtonsRef.current.set(itemId, actionButtons)
        } else {
          actionButtonsRef.current.delete(itemId)
        }
      }
    },
    [],
  )

  useEffect(() => {
    const currentSnapshot: FocusSnapshot = {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent -- snapshot controlled props for post-commit DOM focus recovery
      itemIds: items.map(item => item.id),
      removeVisible,
      sendNowVisible,
    }
    const previousSnapshot = previousSnapshotRef.current
    previousSnapshotRef.current = currentSnapshot

    const focusedAction = focusedActionRef.current
    if (
      !previousSnapshot ||
      !focusedAction ||
      !includesAction(previousSnapshot, focusedAction) ||
      includesAction(currentSnapshot, focusedAction) ||
      !isBrowserFocusLost()
    ) {
      return
    }

    const focusedItemButtons = actionButtonsRef.current.get(focusedAction.itemId)
    const otherAction: ActionName = focusedAction.action === 'remove' ? 'sendNow' : 'remove'
    const otherActionButton = focusedItemButtons?.[otherAction]
    if (otherActionButton?.isConnected) {
      otherActionButton.focus()
      return
    }

    const neighborIds = getNeighborIds(previousSnapshot.itemIds, currentSnapshot.itemIds, focusedAction.itemId)
    const sameActionButton = neighborIds
      .map(itemId => actionButtonsRef.current.get(itemId)?.[focusedAction.action])
      .find(button => button?.isConnected)
    if (sameActionButton) {
      sameActionButton.focus()
      return
    }

    const anyActionButton = neighborIds
      .map(itemId => {
        const actionButtons = actionButtonsRef.current.get(itemId)
        return actionButtons?.sendNow ?? actionButtons?.remove
      })
      .find(button => button?.isConnected)
    if (anyActionButton) {
      anyActionButton.focus()
      return
    }

    if (triggerRef.current?.isConnected) {
      triggerRef.current.focus()
      return
    }

    const returnFocusElement = returnFocusRef?.current
    if (returnFocusElement?.isConnected) {
      returnFocusElement.focus()
      return
    }

    onFocusFallback?.()
  }, [items, onFocusFallback, removeVisible, returnFocusRef, sendNowVisible])

  const handleExpandedChange = () => {
    const nextExpanded = !isExpanded
    if (!isControlled) setUncontrolledExpanded(nextExpanded)
    onExpandedChange?.(nextExpanded)
  }

  if (items.length === 0) return null

  return (
    <div ref={ref} className={clsx(styles.QueuedMessageControl, className)}>
      <section className={styles.Section} aria-label={ariaLabel}>
        <Button
          ref={triggerRef}
          className={styles.Disclosure}
          type="button"
          size="small"
          count={items.length}
          aria-expanded={isExpanded}
          onClick={handleExpandedChange}
        >
          Queued
        </Button>
        {isExpanded ? (
          <ol className={styles.List}>
            {items.map((item, index) => (
              <QueuedMessageItem
                key={item.id}
                item={item}
                itemCount={items.length}
                itemPosition={index + 1}
                onActionFocus={handleActionFocus}
                onActionRef={handleActionRef}
                onRemove={removeVisible ? onRemove : undefined}
                onSendNow={sendNowVisible ? onSendNow : undefined}
                onSendNowIntent={onSendNowIntent}
              />
            ))}
          </ol>
        ) : null}
      </section>
    </div>
  )
}

QueuedMessageControl.displayName = 'QueuedMessageControl'
